import { useState, type ReactNode } from 'react';

interface ChecklistItemProps {
  id: string;
  title: string;
  description?: string;
  whatThisIs?: string;
  whyItMatters?: string;
  bulletPoints?: string[];
  checked: boolean;
  onChange: (id: string, checked: boolean) => void;
  disabled?: boolean;
  children?: ReactNode;
  trackedCount?: number;
  onAddToTracker?: () => void;
}

export default function ChecklistItem({
  id,
  title,
  description,
  whatThisIs,
  whyItMatters,
  bulletPoints,
  checked,
  onChange,
  disabled,
  children,
  trackedCount = 0,
  onAddToTracker,
}: ChecklistItemProps) {
  const hasExpandableContent = !!(whatThisIs || whyItMatters || (bulletPoints && bulletPoints.length > 0) || children);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`w-full rounded-2xl border-2 transition-all overflow-hidden bg-white ${
        checked ? 'border-cyan-700' : 'border-stone-200'
      } ${disabled ? 'opacity-70' : ''}`}
    >
      <div className="flex items-center justify-between px-8 py-8 gap-4">
        <div
          className={`flex-1 ${hasExpandableContent && !disabled ? 'cursor-pointer' : ''}`}
          onClick={() => hasExpandableContent && !disabled && setIsExpanded(!isExpanded)}
        >
          <h3 className="font-semibold text-[22px] text-cyan-900 tracking-tight leading-none">
            {title}
          </h3>
          {description && (
            <p className="text-sm text-stone-500 mt-2 leading-relaxed">
              {description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-6 shrink-0">
          {hasExpandableContent && (
            <button
              type="button"
              onClick={() => !disabled && setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-stone-800 hover:text-stone-800 transition-colors focus:outline-none"
            >
              <span className="text-sm font-medium">
                {isExpanded ? 'View less' : 'View more'}
              </span>
              <svg
                className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}

          <div className="flex items-center gap-3 shrink-0">
            {onAddToTracker && (
              <div className="flex items-center gap-2">
                {trackedCount > 0 && (
                  <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded">
                    Tracked {trackedCount > 1 ? `(${trackedCount})` : ''}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => !disabled && onAddToTracker()}
                  className="text-xs font-medium text-teal-700 hover:text-teal-900 transition-colors whitespace-nowrap"
                  disabled={disabled}
                >
                  + Add to Tracker
                </button>
              </div>
            )}
          </div>

          <div
            role="checkbox"
            aria-checked={checked}
            className={`w-[44px] h-[44px] rounded-[14px] border-2 flex items-center justify-center transition-all bg-white ${
              checked ? 'border-cyan-700' : 'border-stone-200 hover:border-stone-300'
            } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onChange(id, !checked);
            }}
          >
            {checked && (
              <svg className="w-5 h-5 text-cyan-700" viewBox="0 0 24 24" fill="none">
                <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
      </div>

      {isExpanded && hasExpandableContent && (
        <div className="px-8 pb-10 pt-4 border-t border-stone-100">
          <div className="space-y-8">
            {whatThisIs && (
              <div>
                <p className="text-[12px] font-bold uppercase tracking-widest text-stone-800 mb-3">What this is</p>
                <p className="text-[15px] text-stone-600 leading-relaxed">{whatThisIs}</p>
              </div>
            )}
            {whyItMatters && (
              <div>
                <p className="text-[12px] font-bold uppercase tracking-widest text-stone-800 mb-3">Why it matters</p>
                <p className="text-[15px] text-stone-600 leading-relaxed">{whyItMatters}</p>
              </div>
            )}
            {bulletPoints && bulletPoints.length > 0 && (
              <div>
                <p className="text-[12px] font-bold uppercase tracking-widest text-stone-800 mb-3">Key Actions</p>
                <ul className="space-y-3">
                  {bulletPoints.map((point, i) => (
                    <li key={i} className="text-[15px] text-stone-600 flex items-start gap-4">
                      <span className="text-stone-600 text-xl leading-none">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {children}
          </div>
        </div>
      )}
    </div>
  );
}